/**
 * Speed Role - Constants
 *
 * Fatores de conversão, símbolos e aliases para unidades de velocidade.
 *
 * Fontes:
 * - NIST SP 811 (2008)
 * - SI Brochure, 9th Edition (2019)
 * - International Yard and Pound Agreement (1959)
 */

import type { SimpleUnitConfig, UnitAliases } from "../../contracts";

// =============================================================================
// TYPES
// =============================================================================

export type SpeedUnit =
  // SI
  | "meter_per_second"
  | "kilometer_per_hour"
  // Imperial/US
  | "mile_per_hour"
  | "foot_per_second"
  // Nautical
  | "knot"
  // Scientific
  | "mach"
  | "speed_of_light";

// =============================================================================
// UNITS CONFIG
// =============================================================================

/**
 * Configuração de cada unidade de velocidade.
 *
 * Fatores exatos:
 * - km/h = 1000/3600 m/s = 0.277777... m/s
 * - mph = 1609.344/3600 m/s = 0.44704 m/s (exato, derivado de mile)
 * - ft/s = 0.3048 m/s (exato, derivado de foot)
 * - knot = 1852/3600 m/s = 0.514444... m/s (exato, derivado de nautical mile)
 *
 * Fatores aproximados:
 * - Mach: varia com temperatura/altitude, usando 340.29 m/s (nível do mar, 15°C)
 * - Velocidade da luz: exato por definição SI (299792458 m/s)
 */
export const SPEED_UNITS: Record<SpeedUnit, SimpleUnitConfig> = {
  // SI Units
  meter_per_second: {
    factor: 1,
    symbol: "m/s",
    singular: "meter per second",
    plural: "meters per second",
  },
  kilometer_per_hour: {
    factor: 1000 / 3600, // 0.277777...
    symbol: "km/h",
    singular: "kilometer per hour",
    plural: "kilometers per hour",
  },

  // Imperial/US (exatos desde 1959)
  mile_per_hour: {
    factor: 0.44704, // 1609.344 / 3600 (exato)
    symbol: "mph",
    singular: "mile per hour",
    plural: "miles per hour",
  },
  foot_per_second: {
    factor: 0.3048, // exato
    symbol: "ft/s",
    singular: "foot per second",
    plural: "feet per second",
  },

  // Nautical (exato)
  knot: {
    factor: 1852 / 3600, // 0.514444...
    symbol: "kn",
    singular: "knot",
    plural: "knots",
  },

  // Scientific
  mach: {
    factor: 340.29, // velocidade do som ao nível do mar, 15°C (aproximado)
    symbol: "Ma",
    singular: "mach",
    plural: "mach",
  },
  speed_of_light: {
    factor: 299792458, // exato por definição SI
    symbol: "c",
    singular: "speed of light",
    plural: "speed of light",
  },
};

// =============================================================================
// ALIASES
// =============================================================================

/**
 * Aliases para reconhecimento de unidades no cast
 */
export const SPEED_ALIASES: UnitAliases = {
  // meter_per_second
  "m/s": "meter_per_second",
  mps: "meter_per_second",
  // kilometer_per_hour
  "km/h": "kilometer_per_hour",
  kmh: "kilometer_per_hour",
  kph: "kilometer_per_hour",
  // mile_per_hour
  mph: "mile_per_hour",
  "mi/h": "mile_per_hour",
  // foot_per_second
  "ft/s": "foot_per_second",
  fps: "foot_per_second",
  // knot
  kn: "knot",
  kt: "knot",
  kts: "knot",
  // mach
  ma: "mach",
  // speed_of_light
  c: "speed_of_light",
};

// =============================================================================
// HELPERS
// =============================================================================

/** Lista de todas as unidades */
export const SPEED_UNIT_LIST = Object.keys(SPEED_UNITS) as SpeedUnit[];

/** Fatores de conversão (para compatibilidade) */
export const SPEED_FACTORS: Record<SpeedUnit, number> = Object.fromEntries(
  Object.entries(SPEED_UNITS).map(([unit, config]) => [
    unit,
    config.factor ?? 1,
  ]),
) as Record<SpeedUnit, number>;
